import React, { useEffect, useState } from 'react';
import UnityGame from '../UnityGame/UnityGame';
import IntersectEventMobile from './IntersectEventMobile';
import IntersectEventDesktopFallback from './IntersectEventDesktopFallback';
import API from '../../API';

interface ShowConfig {
  upc_url: string;
  world_map_content_id: string;
  upc_world_map_content_url: string;
  content_load_url: string;
  audible_distance: string;
  conversational_distance: string;
  default_zoom_eval: string;
  zoom_max: string;
}

const TOKEN_STORAGE_KEY = 'intersect.accessToken';

// Mobile detection utility
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Only check user agent for mobile phones (exclude tablets like iPad)
  // This is very conservative - only triggers for actual mobile phone user agents
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobilePhoneRegex = /android.*mobile|iphone|ipod|blackberry|iemobile|opera mini/i;
  
  // Check if it's a mobile phone user agent AND has a small screen
  const isMobileUserAgent = mobilePhoneRegex.test(userAgent.toLowerCase());
  const hasSmallScreen = window.innerWidth <= 768;
  
  // Only return true if BOTH conditions are met
  return isMobileUserAgent && hasSmallScreen;
};

const IntersectEvent: React.FC = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [showRelaunchMessage, setShowRelaunchMessage] = useState<boolean>(false);
  const [eventConfig, setEventConfig] = useState<ShowConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [hasWebGPU, setHasWebGPU] = useState<boolean | null>(null);

  // Initialize token from sessionStorage and detect mobile on mount
  useEffect(() => {
    const cachedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (cachedToken) {
      setAccessToken(cachedToken);
    }
    
    // Detect mobile on mount
    setIsMobile(isMobileDevice());
    
    // Check for WebGPU support
    const checkWebGPU = async () => {
      if (typeof window === 'undefined' || !navigator.gpu) {
        setHasWebGPU(false);
        return;
      }
      
      try {
        const adapter = await navigator.gpu.requestAdapter();
        setHasWebGPU(!!adapter);
      } catch (error) {
        console.error('[IntersectEvent] Error checking WebGPU support:', error);
        setHasWebGPU(false);
      }
    };
    
    checkWebGPU();
    
    // Re-check on resize
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch event configuration from API - only after we have an access token
  useEffect(() => {
    if (!accessToken) {
      // Wait for token before fetching config
      return;
    }

    const fetchEventConfig = async () => {
      try {
        setIsLoadingConfig(true);
        const response = await API.getActiveEventIntersect(accessToken);
        
        if (response.status !== 200) {
          throw new Error(`Failed to fetch event config: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('[IntersectEvent] Loaded event config:', data);
        setEventConfig(data);
        setConfigError(null);
      } catch (error) {
        console.error('[IntersectEvent] Error fetching event config:', error);
        setConfigError('Failed to load event configuration. Please try again later.');
      } finally {
        setIsLoadingConfig(false);
      }
    };

    fetchEventConfig();
  }, [accessToken]);

  // Timer to show relaunch message after 5 seconds if no token
  useEffect(() => {
    if (accessToken) {
      // If we have a token, don't show the relaunch message
      setShowRelaunchMessage(false);
      return;
    }

    // Set a timer for 5 seconds
    const timer = setTimeout(() => {
      setShowRelaunchMessage(true);
    }, 20000);

    // Cleanup timer if component unmounts or token arrives
    return () => clearTimeout(timer);
  }, [accessToken]);

  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('IntersectEvent: received message', event);
      } else {
        console.info('IntersectEvent: received access token message');
      }

      if (!event.data?.accessToken || typeof event.data.accessToken !== 'string') {
        return;
      }

      sessionStorage.setItem(TOKEN_STORAGE_KEY, event.data.accessToken);
      setAccessToken(event.data.accessToken);
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, []);

  // Show loading state while fetching event config
  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          <div className="text-gray-400 mb-4">Loading event configuration...</div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error only if there's an actual error from the API
  if (configError) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-6">
            <div className="text-red-400 font-semibold mb-3">
              ⚠️ Configuration Error
            </div>
            <div className="text-gray-300 mb-4">
              {configError}
            </div>
            <div className="text-gray-400 text-sm">
              Please try refreshing the page or contact support if the issue persists.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          {!showRelaunchMessage ? (
            <>
              <div className="text-gray-400 mb-4">Waiting for authentication from Intersect...</div>
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
              </div>
            </>
          ) : (
            <div className="bg-orange-900 bg-opacity-30 border border-orange-500 rounded-lg p-6">
              <div className="text-orange-400 font-semibold mb-3">
                ⚠️ Authentication Timeout
              </div>
              <div className="text-gray-300 mb-4">
                Authentication is taking longer than expected.
              </div>
              <div className="text-gray-400 text-sm">
                Please close this page and relaunch from the Intersect website to try again.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If we have token but no config yet, we must be loading it
  if (!eventConfig) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          <div className="text-gray-400 mb-4">Loading event configuration...</div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        </div>
      </div>
    );
  }

  // Render mobile view for mobile devices
  if (isMobile) {
    return <IntersectEventMobile accessToken={accessToken} />;
  }

  // Check if we're still determining WebGPU support
  if (hasWebGPU === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          <div className="text-gray-400 mb-4">Checking browser compatibility...</div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        </div>
      </div>
    );
  }

  // Render desktop fallback view if WebGPU is not supported
  if (!hasWebGPU) {
    return <IntersectEventDesktopFallback accessToken={accessToken} />;
  }

  // Render desktop Unity view for desktop devices with WebGPU
  return (
    <UnityGame 
      accessToken={accessToken} 
      show={eventConfig}
    />
  );
};

export default IntersectEvent;
